import type { QuizBuilderQuestionSchema } from '@repo/schemas/quiz/quizBuilderQuestion';
import type { QuestionType } from '@repo/types/enums/questionType';

import { getQuestionDefaultValues } from '@repo/lib/quiz/questions/getQuestionDefaultValue';
import { BadgeQuestionMarkIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { questionsLabel } from '@/lib/questions-labels';

interface QuizBuilderQuestionDialogProps {
  /**
   * Called when the user selects a question type.
   */
  onSelect: (defaultValues: QuizBuilderQuestionSchema) => void;
  /**
   * Optionally control whether the dialog is open.
   */
  open: boolean;
  /**
   * Handler for controlled open state.
   */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog that shows a list of question types for the quiz builder.
 *
 * Usage:
 * - Wrap any clickable UI (e.g. "Dodaj pytanie" button, separator line) in `trigger`.
 * - `onSelect` will be called with default values for the chosen question type.
 * - The dialog automatically closes after selection.
 */
export default function QuizBuilderQuestionDialog(quizBuilderQuestionDialogProps: QuizBuilderQuestionDialogProps) {
  const { onSelect, open, onOpenChange } = quizBuilderQuestionDialogProps;

  const handleSelect = (type: QuestionType) => {
    const defaults = getQuestionDefaultValues(type);
    onSelect(defaults);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-md max-w-md">
        <DialogBody>
          <DialogHeader className={'border-border-primary bg-card gap-2 border-b'}>
            <FeaturedIcon size="lg">
              <BadgeQuestionMarkIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <DialogTitle>Wybierz typ pytania</DialogTitle>
              <DialogDescription className={'text-muted-foreground text-xs'}>Wybierz typ pytania, aby dodać pytanie do quizu.</DialogDescription>
            </div>
          </DialogHeader>
          <div className="flex flex-col gap-1 overflow-auto p-4">
            {Object.entries(questionsLabel).map(([key, question]) => (
              <Button key={key} type="button" variant={'ghost'} className="h-18 min-h-18 justify-start gap-3 text-start" onClick={() => handleSelect(key as QuestionType)}>
                <question.icon />
                <div className="flex flex-col gap-1 overflow-hidden">
                  <span>{question.label}</span>
                  <small className="text-muted-foreground text-xs text-wrap">{question.desc}</small>
                </div>
              </Button>
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
